import { ReactFeatureApp } from '@feature-hub/react';

// Global type declaration
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __MTN_APP_INSTANCE__?: ReactFeatureApp;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __MTN_SERVICES_INITIALIZED__?: boolean;
  }
}

/**
 * Enhanced singleton pattern for Feature Hub app instance management.
 *
 * This class prevents double rendering issues in micro-frontend architectures
 * where Feature Hub may call the create function multiple times, leading to
 * React Hook errors and duplicate service initialization.
 *
 * Key features:
 * - Singleton pattern ensures only one instance exists
 * - Global window fallback for cross-context persistence
 * - Service initialization tracking to prevent duplicate setup
 * - Memory management with cleanup methods
 * - Development logging for debugging
 */
export class FeatureAppInstanceManager {
  private static instance: FeatureAppInstanceManager;
  private appInstance: ReactFeatureApp | null = null;
  private servicesInitialized: boolean = false;
  private initializationTimestamp: number | null = null;

  private constructor() {}

  public static getInstance(): FeatureAppInstanceManager {
    if (!FeatureAppInstanceManager.instance) {
      FeatureAppInstanceManager.instance = new FeatureAppInstanceManager();
    }
    return FeatureAppInstanceManager.instance;
  }

  public getAppInstance(): ReactFeatureApp | null {
    return this.appInstance ?? window.__MTN_APP_INSTANCE__ ?? null;
  }

  public setAppInstance(instance: ReactFeatureApp): void {
    this.appInstance = instance;
    window.__MTN_APP_INSTANCE__ = instance;
    this.initializationTimestamp = Date.now();
  }

  public isInstanceCreated(): boolean {
    return !!this.appInstance || !!window.__MTN_APP_INSTANCE__;
  }

  public areServicesInitialized(): boolean {
    return this.servicesInitialized || !!window.__MTN_SERVICES_INITIALIZED__;
  }

  public markServicesAsInitialized(): void {
    this.servicesInitialized = true;
    window.__MTN_SERVICES_INITIALIZED__ = true;
  }

  public getInitializationInfo(): { timestamp: number | null; age: number | null } {
    const timestamp =
      this.initializationTimestamp ?? (window.__MTN_APP_INSTANCE__ ? Date.now() : null);
    return {
      timestamp,
      age: timestamp ? Date.now() - timestamp : null,
    };
  }

  /**
   * Cleanup method for better memory management.
   * Should be called when the app is unmounted or in testing scenarios.
   */
  public cleanup(): void {
    this.appInstance = null;
    this.servicesInitialized = false;
    this.initializationTimestamp = null;

    // Clean global state
    delete window.__MTN_APP_INSTANCE__;
    delete window.__MTN_SERVICES_INITIALIZED__;

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('🧹 FeatureAppInstanceManager cleaned up');
    }
  }

  /**
   * Reset method for testing scenarios.
   * This will force recreation of the singleton instance.
   */
  public static reset(): void {
    if (FeatureAppInstanceManager.instance) {
      FeatureAppInstanceManager.instance.cleanup();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FeatureAppInstanceManager.instance = null as any;
  }
}

/**
 * Utility function for testing - resets the singleton state.
 * This should only be used in test environments.
 *
 * @example
 * ```typescript
 * // In your test setup
 * import { resetFeatureAppManager } from './FeatureAppInstanceManager';
 *
 * beforeEach(() => {
 *   resetFeatureAppManager();
 * });
 * ```
 */
export function resetFeatureAppManager(): void {
  if (process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development') {
    FeatureAppInstanceManager.reset();
  } else {
    // eslint-disable-next-line no-console
    console.warn('⚠️ resetFeatureAppManager should only be called in test environments');
  }
}

/**
 * Utility function to get the current instance manager for debugging.
 * This should only be used in development or testing.
 */
export function getFeatureAppManager(): FeatureAppInstanceManager {
  return FeatureAppInstanceManager.getInstance();
}
